import { useState, useCallback } from 'react';
import axios from 'axios';
import { API, storage } from '../lib/utils';
import { toast } from 'sonner';

export const useApi = () => {
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = storage.get('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const request = useCallback(async (method, endpoint, data = null, options = {}) => {
    setLoading(true);
    try {
      const config = {
        method,
        url: `${API}${endpoint}`,
        headers: { ...getAuthHeaders(), ...options.headers },
        params: options.params || {},
        ...options
      };

      if (data && ['post', 'put', 'patch'].includes(method.toLowerCase())) {
        config.data = data;
      }

      const response = await axios(config);
      return { data: response.data, error: null };
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Something went wrong';
      if (!options.silent) {
        toast.error(message);
      }
      return { data: null, error: message };
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const get = useCallback((endpoint, options) => request('get', endpoint, null, options), [request]);
  const post = useCallback((endpoint, data, options) => request('post', endpoint, data, options), [request]);
  const put = useCallback((endpoint, data, options) => request('put', endpoint, data, options), [request]);
  const del = useCallback((endpoint, options) => request('delete', endpoint, null, options), [request]);

  return { get, post, put, del, loading };
};

// Specific API hooks
export const useInvoices = () => {
  const api = useApi();

  const getInvoices = useCallback(async (status = null) => {
    const params = status ? { status } : {};
    return api.get('/invoices', { params: { ...params } });
  }, [api]);

  const getInvoice = useCallback(async (id) => {
    return api.get(`/invoices/${id}`);
  }, [api]);

  const createInvoice = useCallback(async (data) => {
    return api.post('/invoices', data);
  }, [api]);

  const updateInvoice = useCallback(async (id, data) => {
    return api.put(`/invoices/${id}`, data);
  }, [api]);

  const deleteInvoice = useCallback(async (id) => {
    return api.del(`/invoices/${id}`);
  }, [api]);

  const updateStatus = useCallback(async (id, status) => {
    return api.put(`/invoices/${id}/status`, null, { params: { status } });
  }, [api]);

  const downloadPdf = useCallback(async (id, invoiceNumber) => {
    const token = storage.get('token');
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      toast.error('Failed to download PDF');
      return { success: false };
    }
  }, []);

  return {
    ...api,
    getInvoices,
    getInvoice,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    updateStatus,
    downloadPdf
  };
};

export const useCustomers = () => {
  const api = useApi();

  const getCustomers = useCallback(async (search = '') => {
    const params = search ? { search } : {};
    return api.get('/customers', { params });
  }, [api]);

  const getCustomer = useCallback(async (id) => {
    return api.get(`/customers/${id}`);
  }, [api]);

  const createCustomer = useCallback(async (data) => {
    return api.post('/customers', data);
  }, [api]);

  const updateCustomer = useCallback(async (id, data) => {
    return api.put(`/customers/${id}`, data);
  }, [api]);

  const deleteCustomer = useCallback(async (id) => {
    return api.del(`/customers/${id}`);
  }, [api]);

  return {
    ...api,
    getCustomers,
    getCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer
  };
};

export const useItems = () => {
  const api = useApi();

  const getItems = useCallback(async (search = '') => {
    const params = search ? { search } : {};
    return api.get('/items', { params });
  }, [api]);

  const getItem = useCallback(async (id) => {
    return api.get(`/items/${id}`);
  }, [api]);

  const createItem = useCallback(async (data) => {
    return api.post('/items', data);
  }, [api]);

  const updateItem = useCallback(async (id, data) => {
    return api.put(`/items/${id}`, data);
  }, [api]);

  const deleteItem = useCallback(async (id) => {
    return api.del(`/items/${id}`);
  }, [api]);

  return {
    ...api,
    getItems,
    getItem,
    createItem,
    updateItem,
    deleteItem
  };
};

export const useCompany = () => {
  const api = useApi();

  const getCompany = useCallback(async () => {
    return api.get('/company');
  }, [api]);

  const updateCompany = useCallback(async (data) => {
    return api.put('/company', data);
  }, [api]);

  return {
    ...api,
    getCompany,
    updateCompany
  };
};

export const useUsage = () => {
  const api = useApi();

  const getUsage = useCallback(async () => {
    return api.get('/usage');
  }, [api]);

  return {
    ...api,
    getUsage
  };
};

export const useAI = () => {
  const api = useApi();

  const extractInvoice = useCallback(async (text) => {
    return api.post('/extract-invoice', { text });
  }, [api]);

  return {
    ...api,
    extractInvoice
  };
};

export const usePaymentConditions = () => {
  const api = useApi();

  const getConditions = useCallback(async (customerId = null) => {
    const params = customerId ? { customer_id: customerId } : {};
    return api.get('/payment-conditions', { params });
  }, [api]);

  return {
    ...api,
    getConditions
  };
};
